<section class="foundation">
  <div class="container">
    <div class="foundation-grid" data-animate>
      <div class="foundation-copy">
        <p class="eyebrow">The Foundation</p>
        <h2 class="foundation-title">The attacker behind TestMachine</h2>
        <p class="foundation-lead">Azimuth gives TestMachine an offensive understanding of how complex systems fail.</p>
        <p>We built the attacker first: an agent capable of exploring systems, reasoning about potential attack paths and validating them through execution.</p>
        <p>That same attacker intelligence now forms the foundation of TestMachine's broader security products — understanding not simply what a system or agent is allowed to do, but what it can ultimately cause to happen.</p>
        <a class="vision-link" href="/products/agis">Learn more about our vision <span aria-hidden="true">→</span></a>
      </div>

      <div class="foundation-diagram" aria-label="Azimuth product radial diagram">
        <svg class="rings" viewBox="0 0 100 100" aria-hidden="true">
          <circle class="ring ring-outer" cx="50" cy="50" r="46" />
          <circle class="ring" cx="50" cy="50" r="36" />
          <circle class="ring" cx="50" cy="50" r="26" />
          <circle class="dot" cx="50" cy="14" r="1.6" />
          <circle class="dot" cx="86" cy="50" r="1.6" />
          <circle class="dot" cx="50" cy="86" r="1.6" />
          <circle class="dot" cx="14" cy="50" r="1.6" />
        </svg>

        <div class="hub">
          <span class="hub-name">Azimuth</span>
          <span class="hub-sub">The attacker</span>
        </div>

        <div class="node node-top">
          <span class="node-name">Agentic Security</span>
          <span class="node-tag">(coming soon)</span>
          <span class="node-sub">From code to autonomous systems</span>
        </div>
        <div class="node node-right">
          <span class="node-name">Continuous Validation</span>
          <span class="node-sub">Ongoing security for evolving systems</span>
        </div>
        <div class="node node-bottom">
          <span class="node-name">Real-World Exploits</span>
          <span class="node-sub">Execution, not just detection</span>
        </div>
        <div class="node node-left">
          <span class="node-name">Protocol Security</span>
          <span class="node-sub">Smart contracts and onchain systems</span>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .foundation {
    padding: var(--section-py) 0;
    background: var(--card-bg);
    border-top: 1px solid var(--border-subtle);
    border-bottom: 1px solid var(--border-subtle);
  }

  .foundation-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: clamp(40px, 6vw, 96px);
    align-items: center;
  }

  .eyebrow {
    font-family: var(--font-mono);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--editorial-muted);
    margin-bottom: 16px;
  }

  .foundation-title {
    font-family: var(--font-display);
    font-size: clamp(1.875rem, 4vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.025em;
    line-height: 1.12;
    color: var(--fg);
    margin-bottom: 28px;
  }

  .foundation-copy p:not(.eyebrow) {
    font-size: 1rem;
    line-height: 1.65;
    color: var(--fg);
    margin-bottom: 18px;
    max-width: 52ch;
  }

  .foundation-copy .foundation-lead {
    font-weight: 700;
  }

  .vision-link {
    display: inline-block;
    margin-top: 14px;
    font-family: var(--font-mono);
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--fg);
    background: var(--editorial);
    text-decoration: none;
    border: 1px solid var(--editorial);
    padding: 12px 20px;
    transition: border-color var(--transition), color var(--transition);
  }

  .vision-link:hover {
    color: var(--fg);
    border-color: var(--fg);
  }

  /* Radial diagram: concentric rings with node cards at the cardinal points,
     matching the reference mockup. */
  .foundation-diagram {
    position: relative;
    aspect-ratio: 1;
    max-width: 560px;
    margin: 0 auto;
    width: 100%;
  }

  .rings {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .ring {
    fill: none;
    stroke: var(--accent);
    stroke-width: 0.25;
    opacity: 0.55;
  }

  .ring-outer {
    stroke: var(--border);
    opacity: 0.7;
  }

  .dot {
    fill: var(--accent);
  }

  .hub {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 34%;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 1px solid var(--editorial);
    background: var(--editorial);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-align: center;
    box-shadow: 0 0 24px color-mix(in srgb, var(--editorial) 68%, transparent), 0 0 72px color-mix(in srgb, var(--editorial) 38%, transparent);
  }

  .hub-name {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: clamp(0.9rem, 1.6vw, 1.15rem);
    color: var(--fg);
  }

  .hub-sub {
    font-family: var(--font-mono);
    font-size: clamp(0.55rem, 1vw, 0.7rem);
    color: var(--fg);
  }

  .node {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    text-align: center;
    padding: 12px 16px;
    border: 1px solid var(--editorial-muted);
    background: var(--editorial-muted);
    border-radius: 8px;
    max-width: 34%;
  }

  .node-top {
    top: -2%;
    left: 50%;
    transform: translateX(-50%);
  }

  .node-right {
    top: 50%;
    right: -2%;
    transform: translateY(-50%);
  }

  .node-bottom {
    bottom: -2%;
    left: 50%;
    transform: translateX(-50%);
  }

  .node-left {
    top: 50%;
    left: -2%;
    transform: translateY(-50%);
  }

  .node-name {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: clamp(0.7rem, 1.4vw, 0.95rem);
    color: var(--bg);
    line-height: 1.25;
  }

  .node-tag {
    font-family: var(--font-mono);
    font-size: clamp(0.55rem, 1vw, 0.7rem);
    color: var(--bg);
  }

  .node-sub {
    font-family: var(--font-mono);
    font-size: clamp(0.55rem, 1vw, 0.7rem);
    color: var(--bg);
    line-height: 1.4;
  }

  @media (max-width: 900px) {
    .foundation-grid {
      grid-template-columns: 1fr;
    }

    .foundation-diagram {
      max-width: 440px;
    }
  }
</style>
